/**
 * The vehicle, as data.
 *
 * Called `Chassis` and never `Hull`, because `hull` is already the hit-point
 * field on `Player` and already an `UpgradeId` in `config.ts`. A third meaning
 * for the same word in the same codebase is how you get a bug nobody can read.
 *
 * Everything a boat *is* lives here: how it handles, what it can carry, where
 * its muzzle and lamp and winch actually are, and what a stock one is capable
 * of before any upgrade. What a boat can *become* stays in `upgrades.ts` — the
 * level curves are shared across every hull, so a level-3 engine means the same
 * proportional thing whatever it is bolted to.
 *
 * `PATHFINDER` is the identity element of the whole refactor. Every value in it
 * is copied verbatim from the `SUB`/`SHIELD`/`NITRO`/`WEAPONS`/`SONAR`/`LIGHT`
 * tables it replaces, never retyped from memory, and `npm run trace` compares
 * the result against a baseline taken before any of this existed. If that
 * golden moves, a value was mistranscribed — which is otherwise a completely
 * silent failure, because a boat that accelerates 3 % differently still looks
 * and plays like a boat.
 */

import {
  LIGHT, NITRO, SHIELD, SONAR, SUB, WEAPONS, type UpgradeId,
} from './config';

export type ChassisId = 'pathfinder' | 'dray';

/**
 * Every offset that has to agree with the mesh, declared beside the mesh that
 * defines it.
 *
 * These were ten literals scattered across three files, and two of them already
 * disagreed: the searchlight's origin was placed at `aim * 2.6` while the light
 * it casts was placed at `aim * 2.8` — same lamp, same hull, two numbers. With
 * one hull that is invisible. With two it becomes a boat whose beam does not
 * come out of its lamp, and it reads as a rendering bug rather than as a typo.
 */
export interface Hardpoints {
  /** Forward weapon muzzle, along the hull axis. */
  muzzle: number;
  /**
   * Where the gun actually is, in hull-local metres — forward of centre and
   * above the axis. Optional: a chassis without a turret leaves both unset and
   * keeps the old behaviour of firing from `muzzle` along the aim.
   */
  muzzleFore?: number;
  /**
   * The turret stand's pivot, hull-local, so the barrel can swing about it.
   *
   * Taken from the rig rather than eyeballed: `Turret_Base` sits at
   * (1.881, 1.034) in the model, and the head that carries the barrel hangs
   * 0.146 below it in the base's own permuted frame. The base is used, which
   * puts the pivot within about 0.15 m of the head's — a tenth of the barrel
   * length it is swinging, and not worth decoding a basis for.
   *
   * Optional, and both halves are required together: a chassis without them
   * fires from a barrel that does not move, which is what every chassis did
   * before the stand was unwelded.
   */
  turretFore?: number;
  turretUp?: number;
  muzzleUp?: number;
  /** How far below the muzzle a torpedo leaves the tube. */
  tubeDrop: number;
  /**
   * The torpedo tube, which is NOT the gun.
   *
   * Torpedoes used to leave from the same point as the cannon — the tip of the
   * nose — which is the one place on a submarine that has never held a tube:
   * the bow is the sonar dome and, on this boat, the searchlight. The reference
   * hull carries two saddle pods on its flanks that are unmistakably tubes, so
   * that is where they launch from now.
   *
   * `x` is along the hull from the centre, `y` below the axis, `z` outboard.
   * The z matters: at the centreline a round launched level with the middle of
   * the boat is inside the hull and the depth buffer eats it until it clears
   * the bow.
   */
  tube: { x: number; y: number; z: number };
  /** How far below the hull centre a depth charge is released. */
  chargeDrop: number;
  /** Searchlight lens, along the axis, and its offset below it. */
  lamp: number;
  lampDrop: number;
  /**
   * Drive exhaust and propeller. Distances AFT, like `wake` and `fairleadAft`
   * and unlike `muzzle` and `lamp` — every axial hardpoint here is a positive
   * magnitude and the call site's sign says which end of the boat it is on.
   *
   * These two were briefly stored negative while the call sites still
   * subtracted them, which put the propeller three and a half metres off the
   * BOW. Signed offsets and signed call sites are a trap; magnitudes are not.
   */
  drive: number;
  prop: number;
  /** Where the wake and thruster bubbles are emitted. */
  wake: number;
  /** How far in front of the lamp the beam march starts, clear of the housing. */
  beamClear: number;
  /** Winch port: aft along the axis, and below the keel. */
  fairleadAft: number;
  fairleadDrop: number;
}

/**
 * Handling. Every field here is a `SUB.*` read that used to sit in `player.ts`,
 * and the whole point of the type is that a second boat can disagree with the
 * first about any of them.
 */
export interface Handling {
  thrustX: number;
  thrustY: number;
  dragX: number;
  dragY: number;
  brakeBoost: number;
  /** Empty-hold vertical acceleration. Positive drifts up. */
  buoyancy: number;
  bankMax: number;
  pitchMax: number;
  attitudeRate: number;
  yawRate: number;
  bounce: number;
  scrapeFriction: number;
  safeImpactSpeed: number;
  impactDamage: number;
}

/**
 * How far the gun mount can elevate and depress, DEGREES, measured off the
 * model rather than chosen.
 *
 * It started symmetric at 60 each way and both ends fouled. The two ends fail
 * for entirely different reasons, so one number could never have been right
 * for both.
 *
 * UP IS A MECHANICAL STOP. Elevating the barrel swings the housing's tail down
 * and forward, straight at the pedestal it is mounted on. Walking the aft-low
 * corner of the head — 0.297 m aft of the trunnion, 0.129 m below it — through
 * the range, against the hull's own centreline profile:
 *
 *     15 deg   tail (1.760, 0.980)   18 mm above the deck
 *     18 deg   tail (1.770, 0.967)    5 mm above the deck
 *     20 deg   tail (1.778, 0.958)    3 mm INTO it
 *     25 deg   tail (1.798, 0.939)   inside the pedestal body
 *
 * DOWN IS NOT MECHANICAL AT ALL. The barrel tip keeps over 320 mm of daylight
 * above the deck even at 40. It is the SHOT: the muzzle ray leaves forward and
 * down over a bow that falls away, and catches it.
 *
 *     30 deg  clears      40 deg  clears by 90 mm at x 2.8      45 deg  HITS
 *
 * So 18 and 32, each with a couple of degrees in hand. On sliders, because
 * both numbers come from a bounding-box CORNER standing in for a rounded
 * casting — which errs tight — and because the only instrument that can
 * actually settle this is someone watching the barrel swing.
 */
export const GUN_LIMIT = { up: 18, down: 32 };

/**
 * Stock capability, before any module is fitted — the half of `statsFor` that
 * used to be a module constant. The level *curves* stay in `upgrades.ts`.
 */
export interface ChassisBase {
  maxHull: number;
  /**
   * Heaviest load this hull can lift with no winch gear fitted, in tonnes.
   *
   * A property of the boat rather than of the gear, because it is: the
   * PATHFINDER is a survey sub with a hook on it and the DRAY is a lighter,
   * and no amount of tackle closes that gap entirely.
   */
  liftCap: number;
  maxEnergy: number;
  energyRegen: number;
  shieldBase: number;
  shieldRegen: number;
  shieldDelay: number;
  nitroMax: number;
  nitroDrain: number;
  cannonCooldown: number;
  cannonDamage: number;
  cannonEnergy: number;
  torpedoMax: number;
  torpedoDamage: number;
  torpedoBlast: number;
  chargeMax: number;
  sonarRange: number;
  sonarCost: number;
  lampRange: number;
  lampIntensity: number;
  lampHalfAngle: number;
}

export interface Chassis {
  id: ChassisId;
  name: string;
  blurb: string;
  /** Salvage price. 0 means it cannot be bought — you start with it. */
  price: number;

  /** Renderer slot names, registered once at construction. */
  mesh: string;
  propMesh: string | null;

  length: number;
  radius: number;
  /** Collision proxies along the hull axis: offset and radius. */
  proxies: readonly { o: number; r: number }[];

  /** Tonnes of boat. Only its ratio to the load matters. */
  dryMass: number;
  /** Tonnes the winch will lift before the line starts complaining. */
  cargoCapacity: number;
  /** Metres per second of line the winch takes in. */
  winch: number;

  handling: Handling;
  hardpoints: Hardpoints;
  base: ChassisBase;

  /**
   * Which modules this boat can fit. A scout with no torpedo bay clamps
   * `torpedo` to 0 however much of one you own — this is what makes a vehicle a
   * constraint rather than a stat line.
   */
  modules: readonly UpgradeId[];

  /**
   * Terminal speeds, precomputed. `player.ts`'s attitude normalisers and
   * `camera.ts`'s look-ahead both divide by these, and both used to hard-code
   * the Pathfinder's numbers.
   */
  readonly terminalX: number;
  readonly terminalY: number;
}

const ALL_MODULES: readonly UpgradeId[] =
  ['hull', 'shield', 'engine', 'cannon', 'torpedo', 'sonar', 'lamp', 'nitro', 'ablative', 'warding', 'winch'];

/**
 * The campaign boat. Every number below is `SUB.*` / `SHIELD.*` / `NITRO.*` /
 * `WEAPONS.*` / `SONAR.*` / `LIGHT.*` by reference rather than by value, so a
 * change to `config.ts` still flows here and nothing can drift.
 */
export const PATHFINDER: Chassis = {
  id: 'pathfinder',
  name: 'PATHFINDER',
  blurb: 'BALANCED SURVEY BOAT',
  price: 0,

  mesh: 'sub',
  propMesh: 'prop',

  length: SUB.length,
  radius: SUB.radius,
  /**
   * MEASURED AGAINST THE MESH, not chosen.
   *
   * `field.ts` opens by promising that what you see is exactly what you hit,
   * and at the bow that was not true: a 1.12 m sphere around a nose whose
   * drawn half-height at that station is 0.63 m. Seventy-eight per cent of
   * empty water, in front of the boat, in the direction it is being flown —
   * which arrives as a gap you can see through and cannot enter.
   *
   * Both hulls were sampled at each station, over a 0.9 m window, ignoring the
   * mast above and the skids below since neither is what wedges in a corridor:
   *
   *              proxy   model   classic
   *   stern      1.24     1.01     1.17
   *   midships   1.42     1.37     1.39
   *   bow        1.12     0.63     0.62
   *
   * Midships was already right and stays. The stern keeps its radius because
   * the CLASSIC hull needs it — the two share this chassis, so a proxy has to
   * cover whichever is flying. Only the bow moves, to the measurement plus a
   * tenth of a metre, and it is the only one that decides whether a nose fits.
   */
  proxies: [
    { o: -SUB.length * 0.34, r: SUB.radius * 1.05 },
    { o: 0, r: SUB.radius * 1.2 },
    { o: SUB.length * 0.34, r: SUB.radius * 0.62 },
  ],

  dryMass: 1.0,
  cargoCapacity: 1.2,
  winch: 7.5,

  handling: {
    thrustX: SUB.thrustX,
    thrustY: SUB.thrustY,
    dragX: SUB.dragX,
    dragY: SUB.dragY,
    brakeBoost: SUB.brakeBoost,
    buoyancy: SUB.buoyancy,
    bankMax: SUB.bankMax,
    pitchMax: SUB.pitchMax,
    attitudeRate: SUB.attitudeRate,
    yawRate: SUB.yawRate,
    bounce: SUB.bounce,
    scrapeFriction: SUB.scrapeFriction,
    safeImpactSpeed: SUB.safeImpactSpeed,
    impactDamage: SUB.impactDamage,
  },

  hardpoints: {
    muzzle: SUB.length * 0.62,
    /**
     * THE LASER FIRES FROM THE LASER, on the hulls that have one.
     *
     * `muzzle` is a distance along the aim from the boat's centre, which is
     * the right shape for a bow gun and the wrong one for a turret: the MK II
     * carries its emitter up on the sail at +1.17 above the axis, so a shot
     * leaving the aim axis came out of the pressure hull a metre below the
     * thing that was supposed to have fired it.
     *
     * Measured off the mesh: the sapphire aperture sits at x 2.377, y 1.173.
     * The shot leaves just forward of it.
     *
     * Present on the chassis rather than on the hull setting because `Player`
     * has no opinion about which mesh is drawn. On the two legacy hulls this
     * lands on the upper forward casing, which is a believable place for a gun
     * on both of them and closer than the old bow point was on either.
     */
    muzzleFore: 2.45,
    muzzleUp: 1.17,
    turretFore: 1.881,
    turretUp: 1.034,
    tubeDrop: 0.8,
    // The saddle pods' forward tip, measured on the reference: t 0.576 along the
    // hull, just below the axis, and 1.15 m outboard where the pods actually sit.
    tube: { x: 0.47, y: 0.12, z: 1.15 },
    chargeDrop: SUB.radius,
    // Reconciled against the mesh: `buildSub` puts the lamp lens at x 2.88,
    // y -0.28. The origin used to be placed at 2.6 and the light it casts at
    // 2.8, neither of which was where the lens is.
    // Reconciled against the RETUNED mesh. The lamp moved from under the chin
    // to just above the axis (the reference has it at +0.015L, the old builder
    // at -0.043L), and the twin nacelles became one ducted prop on the
    // centreline — so the drive glow and the spinning prop both move onto the
    // axis and forward to the duct's throat.
    // Measured stations on the rebuilt mesh: the lens sits at the bell's
    // throat (t 0.928) and the beam leaves its mouth (t 1.0, x 3.10); the
    // ducted prop is at t 0.027, x -2.93.
    lamp: 3.05,
    lampDrop: 0.10,
    drive: 2.86,
    prop: 2.93,
    wake: SUB.length * 0.55,
    beamClear: 3,
    fairleadAft: SUB.length * 0.12,
    fairleadDrop: SUB.radius * 1.15,
  },

  base: {
    maxHull: SUB.maxHull,
    liftCap: 1.5,
    maxEnergy: SUB.maxEnergy,
    energyRegen: SUB.energyRegen,
    shieldBase: SHIELD.base,
    shieldRegen: SHIELD.regen,
    shieldDelay: SHIELD.delay,
    nitroMax: NITRO.max,
    nitroDrain: NITRO.drain,
    cannonCooldown: WEAPONS.cannon.cooldown,
    cannonDamage: WEAPONS.cannon.damage,
    cannonEnergy: WEAPONS.cannon.energy,
    torpedoMax: WEAPONS.torpedo.maxAmmo,
    torpedoDamage: WEAPONS.torpedo.damage,
    torpedoBlast: WEAPONS.torpedo.blastRadius,
    chargeMax: WEAPONS.charge.maxAmmo,
    sonarRange: SONAR.maxRadius,
    sonarCost: SONAR.cost,
    lampRange: LIGHT.searchRange,
    lampIntensity: LIGHT.searchIntensity,
    lampHalfAngle: LIGHT.searchHalfAngle,
  },

  modules: ALL_MODULES,

  terminalX: SUB.thrustX / SUB.dragX,
  terminalY: SUB.thrustY / SUB.dragY,
};

/**
 * The salvager. Not a better Pathfinder — a different tool, and worse at most
 * things on purpose.
 *
 * Four times the hold and a winch that will actually lift a reactor core, in
 * exchange for roughly half the acceleration, a wider turn and a hull that
 * cannot fit a depth-charge rack. It is heavier than it is strong: `dryMass`
 * 2.6 means a 2.4 t load barely troubles it where the same load makes a
 * Pathfinder sink like a stone, which is the entire reason to own one.
 *
 * It is also slightly *positively* trimmed and slower to answer the stick, so
 * it feels like a truck. A player who has flown the Pathfinder for an hour
 * should notice inside three seconds that this is not it.
 */
export const DRAY: Chassis = {
  id: 'dray',
  name: 'DRAY',
  blurb: 'HEAVY RECOVERY TENDER',
  price: 2400,

  mesh: 'dray',
  propMesh: 'prop',

  length: 7.6,
  radius: 2.05,
  proxies: [
    { o: -7.6 * 0.34, r: 2.05 * 1.05 },
    { o: 0, r: 2.05 * 1.2 },
    { o: 7.6 * 0.34, r: 2.05 * 0.95 },
  ],

  dryMass: 2.6,
  cargoCapacity: 5.0,
  winch: 11.0,

  handling: {
    // Terminal speed is thrust/drag, so cutting thrust harder than drag is what
    // makes it slow rather than merely sluggish: 34/3.6 = 9.4 m/s against the
    // Pathfinder's 15.1.
    thrustX: 34,
    thrustY: 26,
    dragX: 3.6,
    dragY: 4.4,
    brakeBoost: 2.0,
    /**
     * Those ballast blisters on the hull are not decoration. Buoyancy 4.0 puts
     * neutral trim at 2.0 t — the boat carries two tonnes without fighting,
     * where a Pathfinder is already sinking at 2.4 m/s² with the same load.
     * That is what "four times the hold" has to mean in the water rather than
     * on a spec sheet, and it is the whole reason to own one.
     */
    buoyancy: 4.0,
    bankMax: 0.36,
    pitchMax: 0.34,
    attitudeRate: 3.6,
    yawRate: 4.4,
    // Heavy and slow: it shrugs off a scrape that would hurt a survey boat.
    bounce: 0.18,
    scrapeFriction: 0.7,
    safeImpactSpeed: 12.0,
    impactDamage: 0.72,
  },

  hardpoints: {
    muzzle: 7.6 * 0.55,
    tubeDrop: 0.9,
    // The Dray is a truck: its tubes are low on the flanks of a much wider hull.
    tube: { x: 0.9, y: 0.5, z: 1.5 },
    chargeDrop: 2.05,
    lamp: 3.2,
    lampDrop: -0.4,
    drive: 2.9,
    prop: 4.3,
    wake: 7.6 * 0.55,
    beamClear: 3.4,
    // The winch is the point of this boat, so it is a real gantry: further aft
    // and further under the keel, where a load can hang clear of the hull.
    fairleadAft: 7.6 * 0.06,
    fairleadDrop: 2.05 * 1.35,
  },

  base: {
    maxHull: 165,
    // A hauler, and it starts as one: bare, the DRAY lifts what a PATHFINDER
    // manages with two levels of gear bought.
    liftCap: 2.6,
    maxEnergy: 120,
    energyRegen: 15,
    shieldBase: 38,
    shieldRegen: 9,
    shieldDelay: 4.2,
    nitroMax: 80,
    nitroDrain: 34,
    // It carries a gun because everything out there bites, not because it is
    // a warship.
    cannonCooldown: WEAPONS.cannon.cooldown * 1.35,
    cannonDamage: WEAPONS.cannon.damage * 0.8,
    cannonEnergy: WEAPONS.cannon.energy * 1.1,
    torpedoMax: 8,
    torpedoDamage: WEAPONS.torpedo.damage,
    torpedoBlast: WEAPONS.torpedo.blastRadius,
    chargeMax: 0,
    // A working boat's sonar and lamp: it has to find things in the dark, and
    // this is the one axis where it beats the Pathfinder outright.
    sonarRange: SONAR.maxRadius * 1.22,
    sonarCost: SONAR.cost * 0.85,
    lampRange: LIGHT.searchRange * 1.18,
    lampIntensity: LIGHT.searchIntensity * 1.1,
    lampHalfAngle: LIGHT.searchHalfAngle * 1.25,
  },

  // No depth-charge rack: a torpedo bay it has, an ordnance bay it does not.
  // The hauler carries the winch as a matter of course. It is what it is for.
  modules: ['hull', 'shield', 'engine', 'cannon', 'sonar', 'lamp', 'nitro', 'ablative', 'winch'],

  terminalX: 34 / 3.6,
  terminalY: 26 / 4.4,
};

export const CHASSIS: Record<ChassisId, Chassis> = {
  pathfinder: PATHFINDER,
  dray: DRAY,
};

export const CHASSIS_LIST: readonly Chassis[] = [PATHFINDER, DRAY];

/** True when this hull can fit that module at all. */
export function fits(c: Chassis, id: UpgradeId): boolean {
  return c.modules.includes(id);
}
